import React, { useState } from "react";
import { Card } from "../card";
import { ListTable } from "../Table/listTable/listTable";
import { studentsListTitle } from "../../Constants/tableTitles/ListTableTitle/studentsList.titles";
import { useStudentsLists } from "../../hooks/students/useStudentsLists.hooks";
import { transformStudentsListTableData } from "../../Services/transformer/transformStudentsListTableData";
import { useNavigate } from "react-router-dom";
import { Paths } from "../../Constants/paths";
import { StudentNavigationHeader } from "../../Constants/Enums/navigationHeader.enum";
import { FilterBar, FilterInput } from "../FilterBar";
import { ClassOptionsDropdown } from "../Classes/ClassOptionsDropdown";
import { useUrlFilters } from "../../hooks/useUrlFilters";
import { BranchOptionsDropdown } from "../Branches/BranchOptionDropdown";

const STUDENT_FILTER_CONFIG = {
  studentName: {
    key: "name",
    defaultValue: "",
    parse: (value: string | null) => value || "",
    serialize: (value: string) => value || null,
  },
  classId: {
    key: "class",
    defaultValue: null as number | null,
    parse: (value: string | null) => (value ? Number(value) : null),
    serialize: (value: number | null) => (value ? String(value) : null),
  },
  branchId: {
    key: "branch",
    defaultValue: null as number | null,
    parse: (value: string | null) => (value ? Number(value) : null),
    serialize: (value: number | null) => (value ? String(value) : null),
  },
} as const;

export const StudentList = () => {
  // Local state for pagination (not in URL)
  const [page, setPage] = useState(1);

  // Use URL filters hook for persistent filter state
  const {
    filters,
    setFilter,
    applyFilters,
    clearFilters,
    hasActiveFilters,
    getActiveFilters,
  } = useUrlFilters(STUDENT_FILTER_CONFIG);

  // Get active filters from URL for API call
  const activeFilters = getActiveFilters();

  const { isLoading: isStudentsListLoading, data: studentsList } =
    useStudentsLists({
      page: page,
      filters: {
        studentName: activeFilters.studentName,
        classId: activeFilters.classId,
        branchId: activeFilters.branchId,
      },
    });

  const navigate = useNavigate();

  const handleSearch = () => {
    // Reset to page 1 when searching
    setPage(1);
    // Apply filters to URL (triggers data refetch via URL change)
    applyFilters();
  };

  const handleClear = () => {
    setPage(1);
    clearFilters();
  };

  const handlePageChange = (pageNo: number) => {
    setPage(pageNo);
  };

  const onClassSelect = (option: any) => {
    setFilter("classId", option.value);
  };

  const onBranchSelect = (option: any) => {
    setFilter("branchId", option.value);
  };

  return (
    <Card cardTitle="Students list">
      <FilterBar
        onSearch={handleSearch}
        onClear={handleClear}
        isLoading={isStudentsListLoading}
        showClearButton={hasActiveFilters()}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8">
          <FilterInput
            label="Student Name"
            placeholder="Search by student name..."
            value={filters.studentName}
            onChange={(value) => setFilter("studentName", value as string)}
          />
          <ClassOptionsDropdown
            onClassSelect={onClassSelect}
            selectedClassId={filters.classId}
          />
          <BranchOptionsDropdown
            onBranchSelect={onBranchSelect}
            selectedBranchId={filters.branchId}
          />
        </div>
      </FilterBar>

      <ListTable
        titles={studentsListTitle}
        data={studentsList?.result || []}
        pagination={{
          onPageChange: handlePageChange,
          totalPage: studentsList?.lastPage || 1,
        }}
        transformFn={transformStudentsListTableData}
        onRowClick={(data: any) => {
          navigate(
            `${Paths.StudentDetails}?section=${StudentNavigationHeader.STUDENT_INFO}&studentId=${data.id}`,
          );
        }}
      />
      <></>
    </Card>
  );
};
